import React, { createContext, useContext, useEffect, useState } from 'react';

import { IAuthContext, UserInfo, UserGroup } from './types';
import { getServerApiClient, getServerBaseUrl } from '@utils/server-client';

const AuthContext = createContext<IAuthContext>({} as IAuthContext);
export const useAuth = () => useContext(AuthContext);

interface AuthWrapperProps {
  children: React.ReactNode;
}
const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPrivateRoute, setIsPrivateRoute] = useState(false);

  const serverApiClient = getServerApiClient();
  const serverBaseURL = getServerBaseUrl() + '/api/auth';

  useEffect(() => {
    const checkSession = async () => {
      try {
        await serverApiClient.get('/api/auth');

        const { data } = await serverApiClient.get('/api/auth/profile');
        const { user, token } = data.userAuthData;
        setUser(parseUserInfo(user));

        const accessToken = token.access_token;
        setToken(accessToken);

        accessToken && setIsAuthenticated(true);
      } catch (error: Error | any) {
        window.location.href = `${serverBaseURL}/login`;
      } finally {
        setLoading(false);
      }
    };
    !isAuthenticated && isPrivateRoute && checkSession();
  }, [isPrivateRoute, isAuthenticated]);

  const parseUserInfo = (userData: any): UserInfo | undefined => {
    if (!userData) return undefined;

    const userGroups = userData['https://clarke.com.br/groups'] as UserGroup[];
    const userRoles = userData['https://clarke.com.br/roles'] as UserInfo['roles'];
    const userPermissions = userData['https://clarke.com.br/permissions'] as UserInfo['permissions'];

    return {
      id: userData['https://clarke.com.br/uuid'],
      name: userData.name,
      email: userData.email,
      pictureUrl: userData.picture,
      isAdmin: userRoles?.includes('admin'),
      isSuperAdmin: userRoles?.includes('admin') || userPermissions?.includes('switch:customer'),
      isCustomer: userRoles?.includes('customer'),
      roles: userData['https://clarke.com.br/roles'],
      groups: userGroups,
      phone: userData.phone_number,
    };
  };

  const logout = async () => {
    try {
      localStorage.removeItem('indication_modal_shown_once');

      await serverApiClient.get('/api/auth/logout');
      window.location.href = `${serverBaseURL}/login`;
      setUser(undefined);
      setIsAuthenticated(false);
    } catch {
      window.location.reload();
    }
  };

  const contextValue: IAuthContext = {
    authStatus: { isAuthenticated, accessToken: token },
    user,
    logout,
    loading,
    token,
    setIsPrivateRoute,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthWrapper;
